import dayjs, { Dayjs } from "dayjs";
import { Facility, TimeSlot } from "../../types/Structure";
import { CompleteBooking } from "../../types/Booking";
import { Col, Modal, Row } from "react-bootstrap";
import { FacilityType } from "../../enums/FacilityType";
import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import "./FreeCalendar.css";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import classNames from "classnames";
import BookingBlock from "./BookingBlock";
import {
  CompletePadelResult,
  CompletePickleballResult,
  CompleteTennisResult,
} from "../../types/Result";
import { useToasts } from "../../contexts/toasts";
import "./FreeCalendar.css";
import { getBookingTypeLabel, isPastDateTime } from "../../utils/stringUtil";
import { Bookings } from "../../api/Booking";
import DurationModal from "../Modals/DurationModal";
import { Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const SLOT_WIDTH = 50;

const getMatchPlayer1Team1Name = (
  match:
    | CompletePadelResult
    | CompleteTennisResult
    | CompletePickleballResult
    | undefined
) => {
  return match && match.player1Team1
    ? `${match.player1Team1?.name || "-"} ${match.player1Team1?.familyName}`
    : "-";
};

const getMatchPlayer2Team1Name = (
  match:
    | CompletePadelResult
    | CompleteTennisResult
    | CompletePickleballResult
    | undefined
) => {
  return match && match.player2Team1
    ? `${match.player2Team1?.name || "-"} ${match.player2Team1?.familyName}`
    : "-";
};

const getMatchPlayer1Team2Name = (
  match:
    | CompletePadelResult
    | CompleteTennisResult
    | CompletePickleballResult
    | undefined
) => {
  return match && match.player1Team2
    ? `${match.player1Team2?.name || "-"} ${match.player1Team2?.familyName}`
    : "-";
};

const getMatchPlayer2Team2Name = (
  match:
    | CompletePadelResult
    | CompleteTennisResult
    | CompletePickleballResult
    | undefined
) => {
  return match && match.player2Team2
    ? `${match.player2Team2?.name || "-"} ${match.player2Team2?.familyName}`
    : "-";
};

type Props = {
  slots: string[];
  facility: Facility;
  date: Dayjs;
  onItemClick?: (
    time: TimeSlot,
    facility: Facility,
    booking?: CompleteBooking
  ) => void;
  onReset?: () => void;
};

type DraggableBookingProps = {
  booking: CompleteBooking;
  children: ReactNode;
  onClick: (e: React.MouseEvent) => void;
};

const DraggableBooking: React.FC<DraggableBookingProps> = ({
  booking,
  children,
  onClick,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "BOOKING",
    item: { booking },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  drag(ref);

  const renderBookingTooltip = useCallback(
    () => (
      <>
        {booking.customType && (
          <Row className="text-center g-0">
            <small className="truncate-text">
              <strong>{booking.customType}</strong>
            </small>
          </Row>
        )}
        {booking.match ? (
          <div>
            {![1, 2, 7].includes(booking.facility.sportId) ? (
              <Row className="text-center g-0">
                <small className="truncate-text">
                  <strong>{booking.notes || ""}</strong>
                </small>
              </Row>
            ) : (
              <>
                {!booking.match?.player1Team1Id &&
                !booking.match?.player2Team1Id &&
                !booking.match?.player1Team2Id &&
                !booking.match?.player2Team2Id ? (
                  <Row className="text-center g-0">
                    <small className="truncate-text">
                      <strong>{booking.notes || ""}</strong>
                    </small>
                  </Row>
                ) : (
                  <>
                    <Row className="text-center g-0">
                      <small className="truncate-text">
                        <strong>
                          {getMatchPlayer1Team1Name(booking.match)}
                        </strong>
                      </small>
                    </Row>
                    <Row className="text-center g-0">
                      <small className="truncate-text">
                        <strong>
                          {getMatchPlayer2Team1Name(booking.match)}
                        </strong>
                      </small>
                    </Row>
                    <Row className="text-center g-0">
                      <small className="truncate-text">
                        <strong>
                          {getMatchPlayer1Team2Name(booking.match)}
                        </strong>
                      </small>
                    </Row>
                    <Row className="text-center g-0">
                      <small className="truncate-text">
                        <strong>
                          {getMatchPlayer2Team2Name(booking.match)}
                        </strong>
                      </small>
                    </Row>
                    <>
                      <Row className="text-center g-0">
                        <strong>Note: </strong>
                      </Row>
                      <Row className="text-center g-0">
                        <span>{booking.notes}</span>
                      </Row>
                    </>
                  </>
                )}
              </>
            )}
          </div>
        ) : (
          <Row className="text-center g-0">
            <small className="truncate-text">
              <strong>
                {booking.createdByHost
                  ? booking.customType
                    ? booking.notes || ""
                    : !booking.notes || booking.notes === ""
                    ? `${getBookingTypeLabel(booking)}`
                    : booking.notes + ""
                  : `${booking.user.name} ${booking.user.familyName}`}
              </strong>
            </small>
          </Row>
        )}
      </>
    ),
    [booking]
  );

  return (
    <Tooltip title={renderBookingTooltip()}>
      <div
        onClick={onClick}
        ref={ref}
        style={{
          opacity: isDragging ? 0.5 : 1,
          cursor: "move",
          display: "inline-block",
        }}
      >
        {children}
      </div>
    </Tooltip>
  );
};

type DropTargetSlotProps = {
  slotIndex: number;
  slots: string[];
  facility: Facility;
  onDropBooking: (
    booking: CompleteBooking,
    targetSlot: string,
    facility: Facility
  ) => void;
  children: ReactNode;
  onClick: (e: React.MouseEvent) => void;
};

export const DropTargetSlot: React.FC<DropTargetSlotProps> = ({
  slotIndex,
  onDropBooking,
  slots,
  facility,
  children,
  onClick,
}) => {
  const dropRef = useRef<HTMLDivElement>(null);
  const [, drop] = useDrop<
    { booking: CompleteBooking; index: number },
    void,
    { isOver: boolean; canDrop: boolean }
  >({
    accept: "BOOKING",
    drop: (item, monitor) => {
      const originSlotIndex = slotIndex;
      const originalSlot = slots[originSlotIndex];
      onDropBooking(item.booking, originalSlot, facility);
    },
  });

  drop(dropRef);

  return (
    <div
      onClick={onClick}
      ref={dropRef}
      style={{
        display: "inline-block",
        width: SLOT_WIDTH,
        verticalAlign: "top",
      }}
    >
      {children}
    </div>
  );
};

export default function FreeCalendar({
  slots,
  facility,
  date,
  onItemClick,
  onReset,
}: Props) {
  const [selectedSlot, setSelectedSlot] = useState<string>();
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [showDurationModal, setShowDurationModal] = useState(false);
  const { showToast } = useToasts();
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const canMoveBookingToSlot = useCallback(
    (
      bookingToMove: CompleteBooking,
      targetSlot: string,
      facility: Facility
    ): boolean => {
      const [startStr, endStr] = bookingToMove.time.split("-");
      const bookingStart = dayjs(`2025-01-01T${startStr}`);
      const bookingEnd = dayjs(`2025-01-01T${endStr}`);
      const bookingDurationMinutes = bookingEnd.diff(bookingStart, "minute");

      const slotStart = dayjs(`2025-01-01T${targetSlot}`);
      const slotEnd = slotStart.add(bookingDurationMinutes, "minute");

      // Verifica sovrapposizione con altre prenotazioni con id diverso
      return !facility.bookings?.some((booking) => {
        if (booking.id === bookingToMove.id) return false;

        const [bStartStr, bEndStr] = booking.time.split("-");
        const bStart = dayjs(`2025-01-01T${bStartStr}`);
        const bEnd = dayjs(`2025-01-01T${bEndStr}`);

        return slotStart.isBefore(bEnd) && slotEnd.isAfter(bStart);
      });
    },
    []
  );

  const handleDropBooking = useCallback(
    (bookingToMove: any, targetSlot: string, facility: Facility) => {
      if (canMoveBookingToSlot(bookingToMove, targetSlot, facility)) {
        const startTime = dayjs(
          `${date.format("YYYY-MM-DD")}T${bookingToMove.time.split("-")[0]}`
        );
        const endTime = dayjs(
          `${date.format("YYYY-MM-DD")}T${bookingToMove.time.split("-")[1]}`
        );
        const durationInMinutes = endTime.diff(startTime, "minute");

        const targetTime = dayjs(`${date.format("YYYY-MM-DD")}T${targetSlot}`);

        let newStartTime, newEndTime;

        if (targetTime.isSameOrBefore(startTime)) {
          newStartTime = targetTime;
          newEndTime = targetTime.add(durationInMinutes, "minute");
        } else if (targetTime.isSameOrAfter(endTime)) {
          newEndTime = targetTime.add(30, "minute");
          newStartTime = newEndTime.subtract(durationInMinutes, "minute");
        } else {
          newStartTime = startTime;
          newEndTime = endTime;
        }

        const newTimeRange = `${newStartTime.format(
          "HH:mm"
        )}-${newEndTime.format("HH:mm")}`;

        Bookings.updateBooking(bookingToMove.id, {
          facilityId: facility.id!,
          time: newTimeRange,
        }).then(
          () => {
            showToast(
              "booking-update-success",
              "Prenotazione correttamente spostata",
              "",
              "success"
            );
            onReset?.();
          },
          () => {
            showToast(
              "booking-update-error",
              "Campo già prenotato, impossibile spostare la prenotazione selezionata",
              "",
              "danger"
            );
          }
        );
      } else {
        showToast(
          "booking-update-error",
          "Campo già prenotato, impossibile spostare la prenotazione selezionata",
          "",
          "danger"
        );
      }
      return { success: true };
    },
    [canMoveBookingToSlot, showToast, date, onReset]
  );

  const getSlotRangeUntilNextBooking = useCallback(
    (slot: string) => {
      const selectedIndex = slots.indexOf(slot);
      if (selectedIndex === -1) return [];

      const bookingStartTimes = facility.bookings?.map(
        (b) => b.time.split("-")[0]
      );

      const nextBookingStart = (bookingStartTimes || [])
        .filter((time) => time > slot)
        .sort()[0];

      // Nessuna prenotazione dopo: restituisci tutto fino alla fine
      if (!nextBookingStart) return slots.slice(selectedIndex);

      const endIndex = slots.indexOf(nextBookingStart);

      if (endIndex === -1) {
        // Se lo startTime della prenotazione non è allineato agli slot esatti
        // possiamo trovare il primo slot *dopo* selectedSlot che è >= startTime
        const fallbackIndex = slots.findIndex(
          (slot, idx) => idx > selectedIndex && slot >= nextBookingStart
        );
        return slots.slice(
          selectedIndex,
          fallbackIndex === -1 ? slots.length : fallbackIndex
        );
      }
      return slots.slice(selectedIndex, endIndex);
    },
    [facility, slots]
  );

  const handleItemClick = useCallback(
    (timeSlot: string, booking?: CompleteBooking) => (e: React.MouseEvent) => {
      // Trova prenotazione che copre questo slot
      e.preventDefault();

      if (!booking && isPastDateTime(date.format("YYYY-MM-DD"), timeSlot))
        return;

      if (!booking) {
        setShowDurationModal(true);
        setSelectedSlot(timeSlot);
        setAvailableSlots(getSlotRangeUntilNextBooking(timeSlot));
        return;
      }

      onItemClick?.({ time: booking.time, maxBookings: 1 }, facility, booking);
    },
    [date, facility, getSlotRangeUntilNextBooking, onItemClick]
  );

  const elements = useMemo(() => {
    let i = 0;
    const result = [];

    while (i < slots.length) {
      const slot = slots[i];

      // Trova prenotazione che copre questo slot
      const matchedBooking = facility.bookings?.find((booking) => {

        const [bookingStartTime, bookingEndTime] = booking.time.split("-");

        const slotStart = dayjs(`${date.format("YYYY-MM-DD")}T${slot}`);
        const slotEnd = slotStart.add(30, "minute");
        const bookingStart = dayjs(
          `${date.format("YYYY-MM-DD")}T${bookingStartTime}`
        );
        let bookingEnd = dayjs(
          `${date.format("YYYY-MM-DD")}T${bookingEndTime}`
        );

        if (bookingEnd.isBefore(bookingStart)) {
          bookingEnd = bookingEnd.add(1, "day");
        }

        return slotStart.isBefore(bookingEnd) && slotEnd.isAfter(bookingStart);
      });

      if (matchedBooking) {
        const [bStart, bEnd] = matchedBooking.time.split("-");
        const bookingStart = dayjs(`${date.format("YYYY-MM-DD")}T${bStart}`);
        let bookingEnd = dayjs(`${date.format("YYYY-MM-DD")}T${bEnd}`);
        
        // Se bookingEnd è prima di bookingStart, significa che la prenotazione supera la mezzanotte
        if (bookingEnd.isBefore(bookingStart)) {
          bookingEnd = bookingEnd.add(1, "day");
        }
        
        const durationSlots = bookingEnd.diff(bookingStart, "minute") / 30;

        // Giocatori della partita (padel/tennis/pickleball) da mostrare nel
        // blocco al posto di titolo/sottotitolo.
        const hasPlayers =
          matchedBooking.match &&
          [1, 2, 7].includes(facility.sportId) &&
          (matchedBooking.match.player1Team1Id ||
            matchedBooking.match.player2Team1Id);
        const players = hasPlayers
          ? [
              getMatchPlayer1Team1Name(matchedBooking.match),
              getMatchPlayer2Team1Name(matchedBooking.match),
              ...(matchedBooking.match?.player1Team2Id
                ? [
                    "vs",
                    getMatchPlayer1Team2Name(matchedBooking.match),
                    getMatchPlayer2Team2Name(matchedBooking.match),
                  ]
                : []),
            ]
          : undefined;

        result.push(
          <DraggableBooking
            onClick={handleItemClick(slot, matchedBooking)}
            key={i}
            booking={matchedBooking}
          >
            <BookingBlock
              booking={matchedBooking}
              width={SLOT_WIDTH * durationSlots + 10}
              players={players}
              className="free-block"
              style={{ cursor: "move" }}
            />
          </DraggableBooking>
        );

        i += durationSlots;
      } else {
        result.push(
          <DropTargetSlot
            key={i}
            slotIndex={i}
            facility={facility}
            slots={slots}
            onDropBooking={handleDropBooking}
            onClick={handleItemClick(slot)}
          >
            <div
              key={i}
              style={{ width: SLOT_WIDTH }}
              className={classNames("bk-free free-block", {
                "bk-free--disabled": isPastDateTime(
                  date.format("YYYY-MM-DD"),
                  slot
                ),
              })}
            >
              <span className="bk-free-time">
                {slot}
                <br />
                <span className="bk-free-end">
                  {dayjs(`${date.format("YYYY-MM-DD")}T${slot}`)
                    .add(30, "minute")
                    .format("HH:mm")}
                </span>
              </span>
            </div>
          </DropTargetSlot>
        );
        i++;
      }
    }

    return result;
  }, [slots, facility, date, handleItemClick, handleDropBooking]);

  const handleCloseDurationModal = () => setShowDurationModal(false);

  const getEndSlot = useCallback(
    (startSlot: string, durationMinutes: number): string => {
      return dayjs(`${date.format("YYYY-MM-DD")}T${startSlot}`)
        .add(durationMinutes, "minute")
        .format("HH:mm");
    },
    [date]
  );

  const handleDurationSubmit = useCallback(
    (duration: number) => {
      handleCloseDurationModal();
      const endSlot = getEndSlot(selectedSlot!, duration);
      const newSlot: TimeSlot = {
        time: `${selectedSlot!}-${endSlot}`,
        maxBookings: 1,
      };
      onItemClick?.(newSlot, facility);
    },
    [selectedSlot, getEndSlot, facility, onItemClick]
  );

  const handleNavigateToBulk = useCallback(() => {
    navigate(`${facility.id}/ricorrenti`);
  }, [navigate, facility]);

  return (
    <div className="mx-auto">
      <Row className="align-items-center justify-content-center mx-auto">
        <Col xs={12} className="mb-2 text-center">
          <span>
            <strong>
              {facility.name}{" "}
              {`(${
                facility.type === FacilityType.Indoor ? "Indoor" : "Outdoor"
              })`}
            </strong>
            <Tooltip title="Programma prenotazione ricorrente">
              <button onClick={handleNavigateToBulk} className="circle-button ms-2">
                <FontAwesomeIcon icon={faCalendar} />
              </button>
            </Tooltip>
          </span>
        </Col>
        <Col
          xs={12}
          className="timeslot-col justify-content-center"
          ref={containerRef}
          id="slots-container"
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 4,
            alignItems: "flex-start",
            paddingLeft: 10,
            paddingRight: 10,
          }}
        >
          {elements}
        </Col>
      </Row>

      <Modal show={showDurationModal} onHide={handleCloseDurationModal}>
        <Modal.Header closeButton>
          <Modal.Title>Inserisci la durata della Prenotazione</Modal.Title>
        </Modal.Header>
        <DurationModal
          slots={availableSlots}
          onSubmit={handleDurationSubmit}
          onClose={handleCloseDurationModal}
        />
      </Modal>
    </div>
  );
}
